/** @jsxImportSource @emotion/react */
import { ClassNames, css } from '@emotion/react';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material';
import debounce from 'debounce-promise';
import React from 'react';

import { Colors, Spacing } from 'src/common/ui/base';
import UserSelectItem from 'src/components/forms/UserSelectItem';
import Autocomplete from 'src/components/forms/controls/autocomplete';
import useRootStore from 'src/stores/useRootStore';

export const NavSearch = () => {
  const { routerStore, searchUsers } = useRootStore();

  const handleGoToUser = targetUser => {
    routerStore.goTo(targetUser.__typename === 'Patient' ? 'showPatient' : 'showProvider', {
      params: { id: targetUser.id },
    });
  };

  const debouncedSearchUsers = debounce(searchUsers, 200);

  return (
    <div css={searchStyles} data-testid="nav-search" role="search">
      <div css={searchIconStyles}>
        <SearchIcon />
      </div>
      <ClassNames>
        {({ css: classNameCss }) => (
          <Autocomplete
            placeholder="Search People..."
            value={null}
            onChange={handleGoToUser}
            disableUnderline
            loadOptions={debouncedSearchUsers}
            classes={{
              root: classNameCss(autocompleteRootStyles),
              input: classNameCss(autocompleteInputStyles),
              placeholder: classNameCss(autocompletePlaceholderStyles),
              singleValue: classNameCss(autocompleteValueStyles),
              valueContainer: classNameCss(autocompleteValueStyles),
            }}
            formatOptionLabel={person => <UserSelectItem person={person} />}
            getOptionLabel={person => {
              const isProvider = person.__typename === 'Provider';
              if (isProvider) {
                return `${person.firstName} ${person.lastName}`;
              } else {
                return `${person.firstName} ${person.preferredName} ${person.lastName}`;
              }
            }}
            getOptionValue={option => option.id}
            async
            withoutIndicators
          />
        )}
      </ClassNames>
    </div>
  );
};

const searchStyles = css`
  background-color: ${alpha(Colors.White, 0.15)};
  border-radius: ${Spacing.Unit1}px;
  flex-basis: 555px;
  min-width: 250px;
  position: relative;

  &:hover {
    background-color: ${alpha(Colors.White, 0.25)};
  }
`;

const searchIconStyles = css`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  width: ${Spacing.Unit4 * 2}px;
`;

const autocompleteRootStyles = css`
  color: inherit;
  width: 100%;
`;

const autocompleteInputStyles = css`
  padding: ${Spacing.Unit2}px;
  padding-left: ${Spacing.Unit4 * 2}px;
  width: 500px;
`;

const autocompleteValueStyles = css`
  color: ${Colors.White}; // TODO should be inherit but doesn't work
  max-width: 100%;
  overflow: hidden;
`;

const autocompletePlaceholderStyles = css`
  color: inherit;
  padding-left: ${Spacing.Unit4 * 2}px;
`;
